import React from 'react';
import { Bot, Sparkles, Calendar, MessageSquare } from 'lucide-react';
import { Link } from 'react-router-dom';

const NewFeatures = () => {
  return (
    <div className="prose dark:prose-invert max-w-none">
      <div className="flex items-center space-x-3 mb-8">
        <div className="p-2 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
          <Sparkles className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
        </div>
        <div>
          <h1 className="mb-0">New Features</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Last updated: March 20, 2025</p>
        </div>
      </div>

      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-8 text-white mb-12">
        <div className="flex items-center space-x-3 mb-4">
          <Bot className="h-8 w-8" />
          <h2 className="text-white m-0">Creator Assistant</h2>
        </div>
        <p className="text-indigo-100 text-lg mb-6">
          Introducing our new AI-powered Creator Assistant - your personal content creation companion.
        </p>
        <Link
          to="/creator-assistant"
          className="inline-flex items-center px-4 py-2 bg-white text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors"
        >
          Try it now
        </Link>
      </div>

      <h2>What's New?</h2>
      
      <h3>AI-Powered Content Creation</h3>
      <p>
        The Creator Assistant uses advanced AI to help you generate, refine, and optimize your content.
        Get instant suggestions, writing assistance, and content ideas tailored to your brand voice and audience.
      </p>

      <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-6 my-8">
        <h4 className="text-lg font-semibold mb-4">Key Features</h4>
        <ul className="space-y-4">
          <li className="flex items-start">
            <MessageSquare className="h-5 w-5 text-indigo-600 dark:text-indigo-400 mt-1 mr-3" />
            <div>
              <strong>Natural Conversations</strong>
              <p>Chat naturally with the AI to brainstorm ideas, get feedback, or solve content challenges.</p>
            </div>
          </li>
          <li className="flex items-start">
            <Calendar className="h-5 w-5 text-indigo-600 dark:text-indigo-400 mt-1 mr-3" />
            <div>
              <strong>Content Planning</strong>
              <p>Get help creating content calendars and scheduling strategies optimized for your audience.</p>
            </div>
          </li>
          <li className="flex items-start">
            <Sparkles className="h-5 w-5 text-indigo-600 dark:text-indigo-400 mt-1 mr-3" />
            <div>
              <strong>Writing Enhancement</strong>
              <p>Improve your content with suggestions for better engagement and readability.</p>
            </div>
          </li>
        </ul>
      </div>

      <h3>How to Get Started</h3>
      <ol>
        <li>Navigate to the Creator Assistant from your dashboard</li>
        <li>Start a conversation by typing your question or request</li>
        <li>Get instant AI-powered responses and suggestions</li>
        <li>Refine and implement the suggestions in your content</li>
      </ol>

      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-6 my-8">
        <h4 className="text-amber-900 dark:text-amber-100 font-semibold mb-2">Pro Tip</h4>
        <p className="text-amber-800 dark:text-amber-200 mb-0">
          For best results, be specific with your requests. Instead of asking "Help me create content",
          try "Help me write an engaging tweet about our new product launch targeting tech enthusiasts".
        </p>
      </div>

      <h3>Coming Soon</h3>
      <p>
        We're continuously improving the Creator Assistant. Here's what's coming next:
      </p>
      <ul>
        <li>Multi-language support</li>
        <li>Custom AI training with your brand voice</li>
        <li>Advanced analytics integration</li>
        <li>Team collaboration features</li>
      </ul>

      <div className="border-t border-gray-200 dark:border-dark-700 mt-12 pt-8">
        <p className="text-gray-600 dark:text-gray-400">
          Have feedback about the Creator Assistant? We'd love to hear from you!{' '}
          <a href="mailto:feedback@serge.com" className="text-indigo-600 dark:text-indigo-400 hover:underline">
            Send us your thoughts
          </a>
        </p>
      </div>
    </div>
  );
};

export default NewFeatures;